/**
 * The one entry point: a named Ember humanoid and a game animation name in, frames out.
 *
 * Pure and deterministic -- no DOM, no pixi -- so the client bakes it into textures, tests read its
 * pixels, and tools write it to PNG, all from the same code.
 */
import {
    EMBER_ARMOR_NAMES,
    EMBER_HELD_NAMES,
    EMBER_PANTS_NAMES,
    EMBER_SHIELD_NAMES,
    EMBER_SHIRT_NAMES,
    EMBER_SHOES_NAMES,
    EmberLook,
    buildPrimitives,
    fistCenter,
    wornArmor,
    wornPants,
    wornShirt,
    wornShoes,
} from "./body"
import { CLIP_FOR_ACTION, EMBER_CLIPS, EmberClip, LPC_ACTION_DURATION_MS, bearingShield, clipTime, layerClip, shambling } from "./clips"
import { faceStamps } from "./face"
import { bodyStamps } from "./marks"
import { EMBER_HUMANOIDS } from "./humanoids"
import { DEFAULT_STYLE, EMBER_STYLES, EmberStyle } from "./styles"
import { FACINGS, FACING_YAW, Facing, Frame, HEAD_COUNTER_TURN, SIDE_OFFSET, STRIDE_ACROSS_SCREEN, facePrimitives, projectPoint, rasterize } from "./raster"
import { DEFAULT_PROPORTIONS, Proportions, solvePose } from "./rig"
import { Vec3, apply, rotY } from "./vec"

export interface EmberAnimation {
    /** The game's name for it, e.g. `walk-left`. */
    name: string
    clip: string
    facing: Facing
    frames: Frame[]
    frameMs: number
    loop: boolean
    /** Ground one cycle covers, for locomotion; the client times the cycle from ground speed. */
    strideCycle?: number
    /** For an attack on the move: where the gait's stride has got to when it ends (0..1). */
    gaitPhaseAfter?: number
}

export const proportionsFor = (overrides?: Partial<Proportions>): Proportions => ({
    ...DEFAULT_PROPORTIONS,
    ...(overrides || {}),
})

/** The skeleton a clip puts a body in at `t`, and the camera it is drawn with, for a facing. */
const posed = (proportions: Proportions, clip: EmberClip, t: number, facing: Facing, style: EmberStyle) => {
    let raster = style.raster
    const pose = clip.sample(t, proportions, facing)
    // In a side facing the head turns back toward profile from the body's three-quarter stance
    // (see SIDE_TURN): toward the near side's opposite, whichever way the figure faces.
    if (facing === "left") pose.headYaw -= HEAD_COUNTER_TURN
    if (facing === "right") pose.headYaw += HEAD_COUNTER_TURN
    if (facing === "left" || facing === "right") {
        const toward = facing === "left" ? -1 : 1
        raster = { ...raster, centerX: raster.centerX + toward * SIDE_OFFSET.x, groundY: raster.groundY - SIDE_OFFSET.y }
    }
    return { skeleton: solvePose(pose, proportions), raster }
}

/** Where on a frame (pixels from its top-left) a body's head and fists are: for effects that follow them. */
export interface EmberJoints {
    head: { x: number; y: number }
    /** Left fist, right fist. */
    hands: [{ x: number; y: number }, { x: number; y: number }]
}

export const clipJoints = (proportions: Proportions, clip: EmberClip, t: number, facing: Facing, style: EmberStyle): EmberJoints => {
    const { skeleton, raster } = posed(proportions, clip, t, facing, style)
    const turn = rotY(FACING_YAW[facing])
    const at = (v: Vec3) => projectPoint(apply(turn, v), raster)
    return { head: at(skeleton.head), hands: [at(fistCenter(skeleton, proportions, "L")), at(fistCenter(skeleton, proportions, "R"))] }
}

export const renderClipFrame = (
    look: EmberLook,
    proportions: Proportions,
    clip: EmberClip,
    t: number,
    facing: Facing,
    style: EmberStyle = EMBER_STYLES[DEFAULT_STYLE],
): Frame => {
    const { skeleton, raster } = posed(proportions, clip, t, facing, style)
    const skin = style.skins[look.skin] ?? style.skins.light
    return rasterize(
        facePrimitives(buildPrimitives(skeleton, proportions, look, facing, style.skins), facing),
        raster,
        [
            // A skull's sockets are its own (body.ts bonesOf): no muscle marks, no eyes.
            ...(style.marks && !look.bones ? bodyStamps(skeleton, proportions, skin, facing, raster) : []),
            ...(look.bones ? [] : faceStamps(skeleton, proportions, skin, facing, look.eyes ?? style.eyes, raster)),
        ],
    )
}

/** `walk-left` -> walk, left. A bare name (no facing) faces the camera. */
/**
 * An attack thrown while walking or running is named `<attack>.<gait>.<phase>[.<speed>]-<facing>`,
 * e.g. `slash.walk.3.60-left`: the attack's upper body over the gait's legs, which start at stride
 * phase `phase / MOVING_ATTACK_PHASES` -- where the walk had got to -- so the legs carry on without
 * a jump, and cover the ground the body crosses at `speed` px/s (rounded to MOVING_ATTACK_SPEED_STEP)
 * while the attack plays, so they keep the walk's own pace instead of churning.
 */
export const MOVING_ATTACK_PHASES = 8
export const MOVING_ATTACK_GAITS = ["walk", "run"]

/** Ground speed (px/s) the legs are timed for when a name gives none. */
export const MOVING_ATTACK_SPEED: Record<string, number> = { walk: 100, run: 140 }
/** Speeds are rounded to this, so a body's pace picks one of a few variants rather than a new one each time. */
export const MOVING_ATTACK_SPEED_STEP = 10

export const movingAttackName = (attack: string, gait: string, phase: number, facing: Facing, speed?: number): string => {
    const at = ((Math.round(phase * MOVING_ATTACK_PHASES) % MOVING_ATTACK_PHASES) + MOVING_ATTACK_PHASES) % MOVING_ATTACK_PHASES
    const pace = speed === undefined ? "" : `.${Math.max(MOVING_ATTACK_SPEED_STEP, Math.round(speed / MOVING_ATTACK_SPEED_STEP) * MOVING_ATTACK_SPEED_STEP)}`
    return `${attack}.${gait}.${at}${pace}-${facing}`
}

export const parseAnimationName = (
    animation: string,
): { action: string; facing: Facing; gait?: string; phase?: number; speed?: number } | undefined => {
    const [name, direction] = animation.split("-")
    const facing = (direction ?? "front") as Facing
    if (!FACINGS.includes(facing)) return undefined
    const [action, gait, phase, pace] = name.split(".")
    if (!(action in CLIP_FOR_ACTION)) return undefined
    if (gait === undefined) return { action, facing }
    const index = Number(phase)
    if (!(action in LPC_ACTION_DURATION_MS) || !MOVING_ATTACK_GAITS.includes(gait)) return undefined
    if (!Number.isInteger(index) || index < 0 || index >= MOVING_ATTACK_PHASES) return undefined
    if (pace === undefined) return { action, facing, gait, phase: index / MOVING_ATTACK_PHASES }
    const speed = Number(pace)
    if (!Number.isInteger(speed) || speed <= 0 || speed > 1000) return undefined
    return { action, facing, gait, phase: index / MOVING_ATTACK_PHASES, speed }
}

/** Everything about an animation except its pixels, plus a way to render them one frame at a time. */
export interface EmberAnimationPlan extends Omit<EmberAnimation, "frames"> {
    frameCount: number
    renderFrame: (i: number) => Frame
    /** Where the head and fists are on frame `i` (cheap: a pose, no raster). */
    jointsAt: (i: number) => EmberJoints
}

/** What the body carries on top of its humanoid's fixed look: equipment, from the entity. */
export interface EmberEquipment {
    /** An EMBER_HELD name (body.ts) for the right hand, or nothing. */
    held?: string
    /** Its colour variant (`metal`, `brown`), as the inventory item names it. */
    heldColor?: string
    /** Worn leg wear, an EMBER_PANTS name (`pantaloons`): drawn in place of the look's own pants. */
    pants?: string
    /** Its colour, a dye name (`maroon`), as the inventory item names it. */
    pantsColor?: string
    /** Worn body armour, an EMBER_ARMOR name (`armor-plate`), over the look's shirt. */
    armor?: string
    /** Its colour (`metal`, `gold`), as the inventory item names it. */
    armorColor?: string
    /** A worn shirt, an EMBER_SHIRT_NAMES type (`longsleeve_laced`), in place of the look's own. */
    shirt?: string
    shirtColor?: string
    /** Worn footwear, an EMBER_SHOES name (`boots`), in place of the look's own. */
    shoes?: string
    /** Its colour (a dye, or for armoured shoes a metal), as the inventory item names it. */
    shoesColor?: string
    /**
     * Hair, from the entity's appearance rather than its inventory: any of the game's styles
     * (body.ts HAIR_DESIGNS) in place of the humanoid's own, and its colour.
     */
    hair?: string
    hairColor?: string
    /** The entity's body colour (appearance.bodyColor): a skeleton's bone takes it (`green`). */
    bodyColor?: string
    /** A shield, an EMBER_SHIELD_NAMES type, on the left arm (slung on the back with a bow in hand). */
    shield?: string
    /** Its colour, as the inventory item names it. */
    shieldColor?: string
}

/**
 * What an Ember body holds and wears, from its entity: the active weapon, the worn shirt, pants,
 * armour, shoes and shield, each when Ember has a drawing of it, and the hair it has chosen. One without is left out rather than guessed at.
 */
export const emberEquipmentOf = (entity: {
    weapon?: { weaponType?: string } | null
    appearance?: { hair?: string; hairColor?: string; bodyColor?: string } | null
    inventory?: { items?: { active?: boolean; itemTypeMeta?: { class?: string; type?: string; color?: string } }[] } | null
}): EmberEquipment => {
    const equipment: EmberEquipment = {}
    const items = entity.inventory?.items ?? []
    const weapon = entity.weapon?.weaponType
    if (weapon && EMBER_HELD_NAMES.includes(weapon)) {
        equipment.held = weapon
        // The colour is the equipped item's: the active one of this type in the inventory.
        const color = items.find(i => i.active && i.itemTypeMeta?.type === weapon)?.itemTypeMeta?.color
        if (color) equipment.heldColor = color
    }
    const pants = items.find(i => i.active && i.itemTypeMeta?.class === "pants")?.itemTypeMeta
    if (pants?.type && EMBER_PANTS_NAMES.includes(pants.type)) {
        equipment.pants = pants.type
        if (pants.color) equipment.pantsColor = pants.color
    }
    const armor = items.find(i => i.active && i.itemTypeMeta?.class === "armor")?.itemTypeMeta
    if (armor?.type && EMBER_ARMOR_NAMES.includes(armor.type)) {
        equipment.armor = armor.type
        if (armor.color) equipment.armorColor = armor.color
    }
    if (entity.appearance?.bodyColor) equipment.bodyColor = entity.appearance.bodyColor
    // Hair the entity has chosen; none chosen leaves the humanoid's own.
    const hair = entity.appearance?.hair
    if (hair) {
        equipment.hair = hair
        if (entity.appearance?.hairColor) equipment.hairColor = entity.appearance.hairColor
    }
    const shirt = items.find(i => i.active && i.itemTypeMeta?.class === "shirt")?.itemTypeMeta
    if (shirt?.type && EMBER_SHIRT_NAMES.includes(shirt.type)) {
        equipment.shirt = shirt.type
        if (shirt.color) equipment.shirtColor = shirt.color
    }
    const shoes = items.find(i => i.active && i.itemTypeMeta?.class === "shoes")?.itemTypeMeta
    if (shoes?.type && EMBER_SHOES_NAMES.includes(shoes.type)) {
        equipment.shoes = shoes.type
        if (shoes.color) equipment.shoesColor = shoes.color
    }
    const shield = items.find(i => i.active && i.itemTypeMeta?.class === "shield")?.itemTypeMeta
    if (shield?.type && EMBER_SHIELD_NAMES.includes(shield.type)) {
        equipment.shield = shield.type
        if (shield.color) equipment.shieldColor = shield.color
    }
    return equipment
}

/** One string per distinct look the equipment draws: a cache key for its frames. */
export const emberEquipmentKey = (e: EmberEquipment): string =>
    [
        e.held, e.heldColor, e.pants, e.pantsColor, e.armor, e.armorColor,
        e.shield, e.shieldColor, e.shoes, e.shoesColor, e.hair, e.hairColor, e.shirt, e.shirtColor, e.bodyColor,
    ]
        .map(v => v ?? "")
        .join(":")

/** The game's (LPC's) human body colours as Ember skins; also which skins are living human ones. */
const HUMAN_SKINS: Record<string, string> = {
    light: "light", white: "light", tan: "olive", olive: "olive", brown: "brown", dark: "black", black: "black",
}

/** The humanoid's look with its equipment on. */
const equippedLook = (look: EmberLook, e: EmberEquipment, slingShield = false): EmberLook => ({
    ...look,
    ...(e.held ? { held: e.held, heldColor: e.heldColor } : {}),
    ...(e.pants ? { pants: wornPants(e.pants, e.pantsColor ?? "brown") } : {}),
    ...(e.armor ? { armor: wornArmor(e.armor, e.armorColor ?? "") } : {}),
    ...(e.shoes ? { shoes: wornShoes(e.shoes, e.shoesColor ?? "brown") } : {}),
    ...(e.shirt ? { shirt: wornShirt(e.shirt, e.shirtColor ?? "white") } : {}),
    ...(e.hair ? { hair: { style: e.hair, color: e.hairColor ?? look.hair?.color ?? "brown" } } : {}),
    // Bone takes the body colour, and so does a living man's skin (a zombie's dead flesh is its own).
    ...(e.bodyColor && look.bones ? { boneColor: e.bodyColor } : {}),
    ...(e.bodyColor && !look.bones && HUMAN_SKINS[look.skin] && HUMAN_SKINS[e.bodyColor] ? { skin: HUMAN_SKINS[e.bodyColor] } : {}),
    ...(e.shield ? { shield: { type: e.shield, color: e.shieldColor ?? "", slung: slingShield || shieldSlung(e) } } : {}),
})

/** A bow needs the left hand: its shield goes on the back. */
const shieldSlung = (e: EmberEquipment) => e.held === "bow"

/**
 * A body holding something is drawn on a bigger frame, the body where a 64px frame puts it and the
 * margin all round for the tool's reach -- an axe raised overhead or swung out at arm's length
 * leaves the 64px body frame. The client centres the bigger frame on the same point, as it does
 * LPC's oversize weapon sheets.
 */
export const HELD_FRAME = 96
/** Bigger still for a blade long enough to stand a body's height over the head in a cut. */
const HELD_FRAMES: Record<string, number> = { longsword: 112, saber: 112 }
export const heldFrame = (held: string): number => HELD_FRAMES[held] ?? HELD_FRAME
/** A shield in hand needs less room than a weapon's swing, but a falling body flings it past 64px. */
export const SHIELD_FRAME = 80

const withFrame = (style: EmberStyle, size: number): EmberStyle => {
    const grow = (size - style.raster.width) / 2
    return {
        ...style,
        raster: {
            ...style.raster,
            width: size,
            height: size,
            centerX: style.raster.centerX + grow,
            groundY: style.raster.groundY + grow,
        },
    }
}

/**
 * Plan an animation without rendering it, so a caller on a frame budget (the client's baker) can
 * spread the frames over several ticks. Undefined for an unknown humanoid or an action no clip covers.
 */
export const planEmberAnimation = (
    spriteName: string,
    animation: string,
    equipment: EmberEquipment = {},
): EmberAnimationPlan | undefined => {
    const humanoid = EMBER_HUMANOIDS[spriteName]
    const parsed = parseAnimationName(animation)
    if (!humanoid || !parsed) return undefined
    const baseStyle = EMBER_STYLES[humanoid.style ?? DEFAULT_STYLE]
    // Held things and shields reach past the 64px body frame: a bigger one, centred on the same point.
    // A bigger body's frame grows its held things' frames by as much: its reach is longer too.
    const growth = (humanoid.frame ?? 64) - 64
    const frame = Math.max(
        equipment.held ? heldFrame(equipment.held) + growth : 64,
        equipment.shield ? SHIELD_FRAME + growth : 64,
        humanoid.frame ?? 64,
    )
    const style = frame > 64 ? withFrame(baseStyle, frame) : baseStyle
    // A weapon with an attack of its own (a dagger's slash, a bow's shot) is keyed `<action>:<held>`
    // or `<clip>:<held>`.
    const own = (name: string) => equipment.held && (style.clips?.[`${name}:${equipment.held}`] ?? EMBER_CLIPS[`${name}:${equipment.held}`])
    const clipOf = (name: string) => own(name) || style.clips?.[name] || EMBER_CLIPS[name]
    const proportions = proportionsFor(humanoid.proportions)
    const strideOf = (c: EmberClip) => (c.strideFor ? c.strideFor(proportions) : c.strideCycle)
    // A shield in hand takes the left arm: carried at the hip, raised in an attack.
    const shielded = !!equipment.shield && !shieldSlung(equipment)
    const attack = LPC_ACTION_DURATION_MS[parsed.action] !== undefined
    // A zombie shambles over every clip but its fall, reaching out when its hands are empty and
    // it is not striking with them.
    const shambles = humanoid.posture === "shamble"
    // A cast's hands are the spell's: neither a shield nor a zombie's reach takes them.
    const casting = parsed.action === "cast"
    // A climber's hands are the ladder's: a shield goes on the back.
    const climbing = parsed.action === "climb"
    const bearing = (c: EmberClip, raised: boolean) => {
        if (c.name === "hurt" || climbing) return c
        const borne = shielded && !casting ? bearingShield(c, raised) : c
        return shambles ? shambling(borne, !raised && !casting && !equipment.held && !shielded) : borne
    }
    let clip = bearing(own(parsed.action) || clipOf(CLIP_FOR_ACTION[parsed.action]), attack)
    let gaitPhaseAfter: number | undefined
    if (parsed.gait) {
        // The legs advance as far through their stride as the attack's duration carries the body.
        const gait = bearing(clipOf(parsed.gait), false)
        const seconds = LPC_ACTION_DURATION_MS[parsed.action] / 1000
        const speed = parsed.speed ?? MOVING_ATTACK_SPEED[parsed.gait]
        const cycles = (seconds * speed) / (strideOf(gait)! * STRIDE_ACROSS_SCREEN[parsed.facing])
        clip = layerClip(clip, gait, parsed.phase!, cycles)
        gaitPhaseAfter = (parsed.phase! + cycles) % 1
    }
    const stride = parsed.gait ? undefined : strideOf(clip)
    // An attack keeps the duration of the LPC animation it replaces, whatever its frame count.
    const lpcDuration = LPC_ACTION_DURATION_MS[parsed.action]
    return {
        name: animation,
        clip: clip.name,
        facing: parsed.facing,
        frameMs: lpcDuration ? lpcDuration / clip.frames : clip.frameMs,
        loop: clip.loop,
        // What the body travels across the screen per cycle with its planted feet keeping still.
        strideCycle: stride && stride * STRIDE_ACROSS_SCREEN[parsed.facing],
        frameCount: clip.frames,
        gaitPhaseAfter,
        renderFrame: i =>
            renderClipFrame(
                equippedLook(humanoid.look, equipment, climbing),
                proportions,
                clip,
                clipTime(clip, i, clip.frames),
                parsed.facing,
                style,
            ),
        jointsAt: i => clipJoints(proportions, clip, clipTime(clip, i, clip.frames), parsed.facing, style),
    }
}

export const generateEmberAnimation = (
    spriteName: string,
    animation: string,
    equipment: EmberEquipment = {},
): EmberAnimation | undefined => {
    const plan = planEmberAnimation(spriteName, animation, equipment)
    if (!plan) return undefined
    const { frameCount, renderFrame, ...meta } = plan
    return { ...meta, frames: Array.from({ length: frameCount }, (_, i) => renderFrame(i)) }
}
